const EARTH_RADIUS_M = 6_371_000

export const DEFAULT_SV_LOCATION = {
  lat: 13.6929,
  lng: -89.2182,
  address: 'San Salvador, El Salvador',
} as const

const EL_SALVADOR_BOUNDS = {
  minLat: 12.9,
  maxLat: 14.6,
  minLon: -90.2,
  maxLon: -87.6,
} as const

const KNOWN_SV_LOCATIONS = [
  {
    aliases: ['santa elena'],
    lat: 13.6744,
    lng: -89.2515,
    address: 'Santa Elena, Antiguo Cuscatlan, La Libertad, El Salvador',
  },
  {
    aliases: ['colonia escalon', 'escalon'],
    lat: 13.7084,
    lng: -89.2445,
    address: 'Colonia Escalon, San Salvador, El Salvador',
  },
  {
    aliases: ['san salvador'],
    ...DEFAULT_SV_LOCATION,
  },
] as const

function toRad(deg: number): number {
  return (deg * Math.PI) / 180
}

function normalizeAddressToken(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

export function isCoordinateInElSalvador(
  lat: number | null | undefined,
  lng: number | null | undefined,
): boolean {
  if (typeof lat !== 'number' || typeof lng !== 'number') return false
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  return (
    lat >= EL_SALVADOR_BOUNDS.minLat &&
    lat <= EL_SALVADOR_BOUNDS.maxLat &&
    lng >= EL_SALVADOR_BOUNDS.minLon &&
    lng <= EL_SALVADOR_BOUNDS.maxLon
  )
}

export function resolveKnownElSalvadorLocation(address: string | null | undefined) {
  const normalized = normalizeAddressToken(address ?? '')
  if (!normalized) return null
  return KNOWN_SV_LOCATIONS.find((item) => item.aliases.some((alias) => normalized.includes(alias)))
}

export function normalizeElSalvadorVehicleLocation(input: {
  homeAddress?: string | null
  homeLat?: number | null
  homeLng?: number | null
}) {
  const homeAddress = input.homeAddress?.trim() ?? ''
  if (isCoordinateInElSalvador(input.homeLat, input.homeLng)) {
    return {
      homeAddress,
      homeLat: input.homeLat as number,
      homeLng: input.homeLng as number,
    }
  }

  const known = resolveKnownElSalvadorLocation(homeAddress)
  if (known) {
    return {
      homeAddress: homeAddress || known.address,
      homeLat: known.lat,
      homeLng: known.lng,
    }
  }

  return {
    homeAddress: homeAddress || DEFAULT_SV_LOCATION.address,
    homeLat: DEFAULT_SV_LOCATION.lat,
    homeLng: DEFAULT_SV_LOCATION.lng,
  }
}

/** Haversine distance in meters */
export function distanceBetween(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}
